import React, { FC, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { Badge, Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FieldModel } from '../../../../../models/form/field';
import { StepModel } from '../../../../../models/form/step';
import Draggable from './utilities/Draggable';
import { getFieldComponent } from './getFieldComponent';
import FieldWrapper from './FieldWrapper';
import Options from './utilities/Options';
import { Rule } from '../../../../../models/form/rule';
import Resizable from './utilities/Resizable';
import useDimension from '../../../../../hooks/useDimension';
import {
  setSelectedFieldById,
  setStep,
  setField,
  addSection,
  updateSection,
  deleteSection,
  selectedFieldSelector,
} from '../../../../../slices/formadmin';
import ModalComponent from '../../../../../components/modal/ModalComponent';
import { FormattedMessage, useIntl } from 'react-intl';
import Label from '../../../../../components/library/inputs/label/Label';
import CheckboxInput from '../../../../../components/library/inputs/checkbox/CheckboxInput';
import { SectionModel } from '../../../../../models/form/section';
import { verifyFieldsSection } from '../rules/Rules';
import { noop } from '../../../../../utilities/FunctionUtilities';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../../../../models/security/security';
import { RootState } from '../../../../../store';
import CultureText from '../../../../../components/culture/CultureText';
import { getText } from '../../../../../utilities/CultureUtilities';
import CultureTextInput from '../../../../../components/culture/CultureTextInput';
import { CultureTextModel } from '../../../../../models/form/form';

interface Props {
  step: StepModel;
  fieldProperties: any;
  rules: Rule[];
}

const FormStepAdmin: FC<Props> = ({ step, fieldProperties, rules }: Props) => {
  const [canDeleteSection, setCanDeleteSection] = useState(false);
  const [sectionMultiple, setSectionMultiple] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [sectionEdition, setSectionEdition] = useState<SectionModel | undefined>(undefined);
  const [sectionName, setSectionName] = useState<CultureTextModel[] | string>('');
  const [dragItem, setDragItem] = useState<FieldModel | undefined>();
  const [swapItem, setSwapItem] = useState<string | undefined>();
  const [isSettings, setIsSettings] = useState(false);
  const [formSecurity, setFormSecurity] = useState<SecurityModel | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const { width } = useDimension(formRef);
  const selectedField = useSelector(selectedFieldSelector);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const localizations = useSelector((state: RootState) => state.formAdmin.form?.localizations) as string[];

  useEffect(() => {
    if (security) {
      setFormSecurity(getSecuritySection(security, SecuritySections.FORM));
    }
  }, [security]);
  const removeDragItem = (stepTemp: StepModel) => {
    if (dragItem) {
      for (let sectionIndex = 0; sectionIndex < stepTemp.sections.length; sectionIndex++) {
        const fields = stepTemp.sections[sectionIndex].fields;
        for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex++) {
          if (dragItem.id === fields[fieldIndex].id) {
            fields.splice(fieldIndex, 1);
            return [sectionIndex, fieldIndex];
          }
        }
      }
    }
    return undefined;
  };

  const swap = (toSwapId: string) => {
    const swapLogic = (stepTemp: StepModel, toSwapId: string, position: number[] | undefined) => {
      if (dragItem) {
        for (let sectionIndex = 0; sectionIndex < stepTemp.sections.length; sectionIndex++) {
          const fields = stepTemp.sections[sectionIndex].fields;
          for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex++) {
            if (toSwapId === fields[fieldIndex].id) {
              if (
                !position ||
                position[0] < sectionIndex ||
                (position[0] === sectionIndex && position[1] > fieldIndex)
              ) {
                fields.splice(fieldIndex, 0, dragItem);
                return;
              } else {
                fields.splice(fieldIndex + 1, 0, dragItem);
                return;
              }
            }
          }
        }
      }
    };

    const stepTemp = _.cloneDeep(step);
    if (dragItem) {
      const position = removeDragItem(stepTemp);
      swapLogic(stepTemp, toSwapId, position);
    }
    dispatch(setStep(stepTemp));
  };

  const handleResize = (newSize: number, field: FieldModel) => {
    dispatch(setField({ ...field, size: newSize }));
    return;
  };

  const backgroundColor = isSettings ? 'rgba(0, 255, 0, 0.2)' : undefined;

  const handleFieldClick = (fieldId?: string) => {
    setIsSettings(true);
    dispatch(setSelectedFieldById(fieldId));
  };

  const addNewSection = () => {
    dispatch(addSection({ name: sectionName, multiple: sectionMultiple }));
    setOpenSettings(false);
  };

  const editStep = () => {
    if (sectionEdition !== undefined) {
      dispatch(updateSection({ id: sectionEdition.id, name: sectionName, multiple: sectionMultiple }));
    }

    setOpenSettings(false);
  };

  const confirmDelete = () => {
    if (sectionEdition !== undefined) {
      dispatch(deleteSection(sectionEdition));
      setOpenSettings(false);
      setOpenDelete(false);
      setSectionEdition(undefined);
    }
  };

  const onDragEnterEmptySection = (index: number) => {
    if (dragItem) {
      const stepTemp = _.cloneDeep(step);
      removeDragItem(stepTemp);
      stepTemp.sections[index].fields = [_.cloneDeep(dragItem)];
      setStep(stepTemp);
    }
  };

  const verifyFieldsAreInRules = (index: number) => {
    if (index !== undefined) {
      const isFieldsInSection = verifyFieldsSection(step.sections[index], rules);
      setCanDeleteSection(!isFieldsInSection);
    } else {
      setCanDeleteSection(false);
    }
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  return (
    <React.Fragment>
      <ModalComponent
        closeSettings={() => setOpenSettings(false)}
        buttons={[
          {
            text: intl.formatMessage({ id: 'multiSections.cancel' }),
            onClick: () => setOpenSettings(false),
            buttonVariant: 'secondary',
          },
          {
            text: intl.formatMessage({ id: 'multiSections.save' }),
            onClick: () => {
              if (sectionEdition === undefined) {
                addNewSection();
              } else {
                editStep();
              }
            },
            buttonVariant: 'primary',
          },
        ]}
        modalHeading={intl.formatMessage({
          id: sectionEdition === undefined ? 'multiSections.addSection' : 'multiSections.editSection',
        })}
        show={openSettings}
      >
        <div>
          <div className="form-group">
            <Label label={intl.formatMessage({ id: 'multiSections.sectionName' })} />
            <CultureTextInput
              id={`section.${sectionEdition?.id}.name`}
              culture={selectedLanguage}
              value={sectionName}
              localizations={localizations}
              onChange={(e) => setSectionName(e)}
            />
          </div>
          <div className="form-group">
            {canDeleteSection && (
              <CheckboxInput
                id="section-multiple-check"
                onChange={(e) => setSectionMultiple(e.target.checked)}
                label={intl.formatMessage({ id: 'multiSections.sectionMultiple' })}
                value={sectionMultiple}
              />
            )}
            {!canDeleteSection && (
              <OverlayTrigger
                placement="right"
                delay={{ show: 50, hide: 100 }}
                defaultShow={false}
                overlay={renderTooltip(intl.formatMessage({ id: 'multiSections.changeToMultiple' }))}
              >
                <span className="d-inline-block">
                  <CheckboxInput
                    id="section-multiple-check"
                    enabled={false}
                    style={{ pointerEvents: 'none' }}
                    onChange={noop}
                    label={intl.formatMessage({ id: 'multiSections.sectionMultiple' })}
                    value={sectionMultiple}
                  />
                </span>
              </OverlayTrigger>
            )}
          </div>
          {sectionEdition !== undefined && (
            <div className="form-group">
              <Label label={intl.formatMessage({ id: 'multiSections.deleteSection' })} />
              <div>
                {canDeleteSection && (
                  <Button variant="danger" onClick={() => setOpenDelete(true)}>
                    {intl.formatMessage({ id: 'multiSections.delete' })}
                  </Button>
                )}
                {!canDeleteSection && (
                  <OverlayTrigger
                    placement="right"
                    delay={{ show: 50, hide: 100 }}
                    defaultShow={false}
                    overlay={renderTooltip(intl.formatMessage({ id: 'multiSections.deleteRule' }))}
                  >
                    <span className="d-inline-block">
                      <Button variant="danger" disabled style={{ pointerEvents: 'none' }}>
                        {intl.formatMessage({ id: 'multiSections.delete' })}
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
              </div>
            </div>
          )}
        </div>
      </ModalComponent>
      {sectionEdition !== undefined && (
        <ModalComponent
          closeSettings={() => setOpenDelete(false)}
          buttons={[
            {
              text: intl.formatMessage({ id: 'multiSections.cancel' }),
              buttonVariant: 'secondary',
              onClick: () => setOpenDelete(false),
            },
            {
              text: intl.formatMessage({ id: 'multiSections.delete' }),
              buttonVariant: 'danger',
              onClick: () => confirmDelete(),
            },
          ]}
          show={openDelete}
          modalHeading={intl.formatMessage({ id: 'multiSections.deleteModalHeader' })}
          modalSize="lg"
        >
          <div className="d-flex justify-content-center flex-column">
            <span>
              <FormattedMessage id="multiSections.deleteConfirmation" />
            </span>
            <span className="field-text">{getText(sectionEdition.name, selectedLanguage)}</span>
          </div>
        </ModalComponent>
      )}
      <div
        className="step-admin"
        onDragOver={(e) => {
          if (e.preventDefault) {
            e.preventDefault();
          }
          const y = e.nativeEvent.clientY;
          if (y > window.innerHeight - 50) {
            window.scrollBy(0, 20);
          } else if (y < 175) {
            window.scrollBy(0, -20);
          }
        }}
        ref={formRef}
      >
        {step.sections.map((section, sectionIndex) => {
          return (
            <div className="section" key={section.id}>
              <div className="section-title">
                <CultureText text={section.name} culture={selectedLanguage}></CultureText>&nbsp;{' '}
                {section.multiple && (
                  <span>
                    <Badge bg="primary">{intl.formatMessage({ id: 'multiSections.multiple' })}</Badge> &nbsp;
                  </span>
                )}
                {formSecurity?.update && (
                  <OverlayTrigger
                    delay={{ show: 50, hide: 100 }}
                    overlay={renderTooltip(intl.formatMessage({ id: 'multiSections.editSection' }))}
                    placement="top"
                  >
                    <span
                      onClick={() => {
                        setSectionMultiple(section.multiple);
                        setSectionEdition(section);
                        verifyFieldsAreInRules(sectionIndex);
                        setSectionName(section.name);
                        setOpenSettings(true);
                      }}
                      className="mdi mdi-pencil-outline"
                      style={{ cursor: 'pointer' }}
                    ></span>
                  </OverlayTrigger>
                )}
              </div>
              <Row className="g-0">
                {section.fields.length === 0 && (
                  <div className="empty-section" onDragEnter={() => onDragEnterEmptySection(sectionIndex)}></div>
                )}
                {section.fields.map((field) => {
                  return (
                    <Col
                      className="fields-col"
                      onClick={() => handleFieldClick(field.id)}
                      md={field.size}
                      key={field.id}
                      style={{
                        height: '100%',
                        border: dragItem?.id === field.id ? '1px dashed lightgray' : '',
                        borderRadius: 4,
                        backgroundColor: selectedField?.id === field.id ? backgroundColor : '',
                        cursor: 'pointer',
                      }}
                    >
                      <Options field={field} rules={rules}>
                        <Resizable
                          parentSize={width}
                          size={field.size}
                          resizeHandler={(newSize) => handleResize(newSize, field)}
                          updateRights={formSecurity?.update}
                        >
                          <Draggable
                            swapedItemId={swapItem}
                            setSwapedItemId={setSwapItem}
                            dragItem={dragItem}
                            field={field}
                            setDragItem={setDragItem}
                            swap={swap}
                            updateRights={formSecurity?.update}
                          >
                            <FieldWrapper
                              id={field.id}
                              hiddenText={field.hiddenText}
                              label={getText(field.text, selectedLanguage)}
                              textColor={field.textColor}
                              size={field.size}
                              required={field.required}
                              visible={field.visible}
                            >
                              {getFieldComponent(field, fieldProperties, selectedLanguage)}
                            </FieldWrapper>
                          </Draggable>
                        </Resizable>
                      </Options>
                    </Col>
                  );
                })}
              </Row>
            </div>
          );
        })}
        {formSecurity?.update && (
          <Button
            onClick={() => {
              setSectionName('');
              setSectionMultiple(false);
              setOpenSettings(true);
              setSectionEdition(undefined);
            }}
            className="add-section-button"
          >
            {intl.formatMessage({ id: 'multiSections.addSection' })}
          </Button>
        )}
      </div>
    </React.Fragment>
  );
};

export default FormStepAdmin;
